import type {Variants} from "framer-motion"
import {motion} from "framer-motion"
import React, {useState} from "react"
import Logo from "../../components/Logo/Logo";
import {MAINTENANCE_MODE} from "../../feature_flags";
import {MaintenanceModal} from "../../components/MaintenanceNotice";
import "../LandingPage/landing.css";

const fadeUp: Variants = {
    hidden: {opacity: 0, y: 36},
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.7,
            ease: "easeOut",
        },
    },
}

const stagger = {
    visible: {
        transition: {
            staggerChildren: 0.12,
        },
    },
}

const demoSections = [
    {
        id: "intake",
        label: "Invention Intake",
        demos: [
            {
                id: "disclosure-upload",
                title: "Invention Disclosures",
                description: "Upload disclosure forms, sketches, and inventor notes. AI extracts the technical core.",
                placeholder: "Demo: Disclosure intake",
                videoUrl: null,
            },
            {
                id: "concept-extraction",
                title: "Concept Extraction",
                description: "Pull out novel features, embodiments, and prior art references automatically.",
                placeholder: "Demo: Extracted concepts panel",
                videoUrl: null,
            },
        ],
    },
    {
        id: "draft",
        label: "Drafting",
        demos: [
            {
                id: "claim-drafting",
                title: "Claim Drafting",
                description: "Generate independent and dependent claim sets from a disclosure. Edit with AI assistance that respects claim differentiation.",
                placeholder: "Demo: Claim builder",
                videoUrl: null,
            },
            {
                id: "spec-drafting",
                title: "Specification Drafting",
                description: "Draft background, summary, and detailed description sections aligned to your claims.",
                placeholder: "Demo: Spec generation",
                videoUrl: null,
            },
            {
                id: "figure-callouts",
                title: "Figure Callouts",
                description: "Reference numerals tracked across the spec and claims. Catches unsupported references before filing.",
                placeholder: "Demo: Reference numeral tracking",
                videoUrl: null,
            },
            {
                id: "consistency-check",
                title: "Consistency Review",
                description: "Flag claim/spec mismatches, unsupported limitations, and antecedent basis issues.",
                placeholder: "Demo: Consistency report",
                videoUrl: null,
            },
        ],
    },
    {
        id: "respond",
        label: "Office Action Response",
        demos: [
            {
                id: "rejection-analysis",
                title: "Rejection Analysis",
                description: "Parse 102/103 rejections and map cited references to specific claim limitations.",
                placeholder: "Demo: Rejection breakdown",
                videoUrl: null,
            },
            {
                id: "response-draft",
                title: "Response Drafting",
                description: "Draft arguments and amendments with citations to the specification and prior art.",
                placeholder: "Demo: Response generator",
                videoUrl: null,
            },
        ],
    },
]

const features = [
    {
        title: "Claim-aware drafting",
        description: "Drafting tools that understand antecedent basis, claim differentiation, and means-plus-function language. Not generic text generation — patent-aware structure from the first draft.",
        icon: (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"/>
            </svg>
        ),
    },
    {
        title: "Office action workflow",
        description: "Map every rejection to the cited references and the specific claim limitations they hit. Draft responses with arguments and amendments grounded in the spec.",
        icon: (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"/>
            </svg>
        ),
    },
    {
        title: "Reference numeral tracking",
        description: "Numerals stay synchronized between claims, spec, and figures. Catches unsupported limitations and orphan numerals before filing.",
        icon: (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
            </svg>
        ),
    },
    {
        title: "Prior art context",
        description: "Pull cited references into the workspace and ground every drafting decision in what&apos;s already on the record. Every suggestion comes with a source.",
        icon: (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75"/>
            </svg>
        ),
    },
]

const trustPoints = [
    {
        title: "Vertex AI",
        description: "Powered by Google&apos;s Vertex AI. Your disclosures are never used for model training.",
    },
    {
        title: "US Data Residency",
        description: "All processing happens in us-central1. Confidential disclosures stay in the United States.",
    },
    {
        title: "Encrypted at Rest",
        description: "Database and document storage encryption protects pre-filing material at every layer.",
    },
]

function DemoCard({demo, size = "default"}: { demo: typeof demoSections[0]["demos"][0], size?: "default" | "large" }) {
    const [isHovered, setIsHovered] = useState(false)
    const isLarge = size === "large"

    return (
        <motion.div
            variants={fadeUp}
            className={`
                relative rounded-lg border border-[#2e2e3a]
                shadow-[0_0_0_1px_rgba(255,255,255,0.02)]
                bg-[#1c1c24]
                bg-[linear-gradient(180deg,rgba(46,46,58,0.35),rgba(28,28,36,0))]
                overflow-hidden
                hover:border-[#c4b5fd]/40
                transition
                group
                ${isLarge ? "md:col-span-2" : ""}
            `}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className={`relative ${isLarge ? "aspect-[2/1]" : "aspect-video"} bg-[#16161c] flex items-center justify-center`}>
                {demo.videoUrl ? (
                    <video
                        src={demo.videoUrl}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                        autoPlay={isHovered}
                    />
                ) : (
                    <div className="p-4 text-center">
                        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#2e2e3a] flex items-center justify-center">
                            <svg className="w-6 h-6 text-[#c4b5fd]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"/>
                            </svg>
                        </div>
                        <span className="text-[#a6a6c2] text-xs">{demo.placeholder}</span>
                    </div>
                )}
            </div>

            <div className="p-5">
                <h3 className="text-base font-medium">{demo.title}</h3>
                <p className="mt-2 text-[#a6a6c2] text-sm leading-relaxed">
                    {demo.description}
                </p>
            </div>
        </motion.div>
    )
}

export default function LandingPageDrafting() {
    const [showMaintenance, setShowMaintenance] = useState(false);
    const onAccessClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (MAINTENANCE_MODE) {
            e.preventDefault();
            setShowMaintenance(true);
        }
    };
    return (
        <main
            className="
                bg-[#1c1c24]
                bg-[radial-gradient(1200px_600px_at_50%_-200px,rgba(196,181,253,0.10),transparent)]
                text-[#ececf3]
                min-h-screen
                overflow-x-hidden
            "
        >
            <div className="pointer-events-none fixed inset-0 z-0">
                <div
                    className="
                        absolute top-[-20%] left-[10%]
                        h-[500px] w-[500px]
                        rounded-full
                        bg-[#c4b5fd]
                        opacity-[0.06]
                        blur-[160px]
                        animate-[float_40s_linear_infinite]
                    "
                />
            </div>

            <div className="absolute top-6 left-8 z-20 flex items-center gap-3">
                <div className="cursor-pointer opacity-90 hover:opacity-100 transition">
                    <Logo/>
                </div>
                <span className="text-[#a6a6c2] text-xs uppercase tracking-[0.2em] border-l border-[#2e2e3a] pl-3">
                    Drafting
                </span>
            </div>

            <section className="relative">
                <div
                    className="pointer-events-none absolute -top-48 left-1/2 h-[700px] w-[700px]
                        -translate-x-1/2 rounded-full bg-[#c4b5fd]
                        opacity-[0.12] blur-[140px]"
                />

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{once: true, margin: "-80px"}}
                    variants={stagger}
                    className="relative mx-auto max-w-6xl px-8 pt-36 pb-24 text-center"
                >
                    <motion.h1
                        variants={fadeUp}
                        className="text-5xl md:text-6xl font-semibold tracking-tight leading-tight"
                    >
                        AI drafting
                        <br/>
                        <span className="text-[#a6a6c2]">
                            for patent prosecution
                        </span>
                    </motion.h1>

                    <motion.p
                        variants={fadeUp}
                        className="mt-8 mx-auto max-w-3xl text-lg text-[#a6a6c2]"
                    >
                        Claim sets, specifications, and office action responses — drafted with AI that
                        understands antecedent basis, reference numerals, and the cited prior art.
                    </motion.p>

                    <motion.div
                        variants={fadeUp}
                        className="mt-14 flex flex-col sm:flex-row justify-center gap-5"
                    >
                        <a
                            href="/app/"
                            onClick={onAccessClick}
                            className="
                                inline-flex items-center justify-center rounded-md
                                bg-[#c4b5fd] px-7 py-3 text-sm font-medium
                                text-[#1c1c24] shadow-lg shadow-[#c4b5fd]/20
                                hover:opacity-90 transition
                            "
                        >
                            Try it out
                        </a>

                        <a
                            href="#contact"
                            className="
                                inline-flex items-center justify-center rounded-md
                                border border-[#2e2e3a]
                                shadow-[0_0_0_1px_rgba(255,255,255,0.02)]
                                px-7 py-3 text-sm
                                hover:bg-[#2e2e3a] transition
                            "
                        >
                            Request a demo
                        </a>
                    </motion.div>
                </motion.div>
            </section>

            <section className="mx-auto max-w-6xl px-8 pb-20">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{once: true, margin: "-80px"}}
                    variants={stagger}
                    className="grid md:grid-cols-3 gap-6"
                >
                    {trustPoints.map((point) => (
                        <motion.div
                            key={point.title}
                            variants={fadeUp}
                            className="
                                flex items-start gap-4 p-5 rounded-lg
                                border border-[#2e2e3a]/50
                                bg-[#1c1c24]/50
                            "
                        >
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#c4b5fd]/10 flex items-center justify-center">
                                <svg className="w-5 h-5 text-[#c4b5fd]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/>
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-medium text-sm">{point.title}</h3>
                                <p className="mt-1 text-[#a6a6c2] text-sm">{point.description}</p>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </section>

            <section className="mx-auto max-w-6xl px-8 py-16">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{once: true, margin: "-80px"}}
                    variants={stagger}
                    className="text-center mb-20"
                >
                    <motion.h2 variants={fadeUp} className="text-3xl font-semibold">
                        The workflow
                    </motion.h2>
                    <motion.p variants={fadeUp} className="mt-4 text-[#a6a6c2] max-w-2xl mx-auto">
                        From invention disclosure to office action response — each step grounded in your
                        own filings and the cited art.
                    </motion.p>
                </motion.div>

                {demoSections.map((section, sectionIndex) => (
                    <motion.div
                        key={section.id}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{once: true, margin: "-80px"}}
                        variants={stagger}
                        className="mb-20 last:mb-0"
                    >
                        <motion.div variants={fadeUp} className="flex items-center gap-4 mb-8">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#c4b5fd]/20 text-[#c4b5fd] text-sm font-medium">
                                {sectionIndex + 1}
                            </div>
                            <h3 className="text-xl font-medium">{section.label}</h3>
                            <div className="flex-1 h-px bg-[#2e2e3a]"/>
                        </motion.div>

                        <motion.div
                            variants={stagger}
                            className={`grid gap-6 ${
                                section.demos.length === 2
                                    ? "md:grid-cols-2"
                                    : section.demos.length === 4
                                        ? "md:grid-cols-2 lg:grid-cols-4"
                                        : "md:grid-cols-2 lg:grid-cols-3"
                            }`}
                        >
                            {section.demos.map((demo) => (
                                <DemoCard key={demo.id} demo={demo}/>
                            ))}
                        </motion.div>
                    </motion.div>
                ))}
            </section>

            <section className="mx-auto max-w-6xl px-8 py-20">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{once: true, margin: "-80px"}}
                    variants={stagger}
                >
                    <motion.div variants={fadeUp} className="text-center mb-16">
                        <h2 className="text-3xl font-semibold">Built for how patent attorneys work</h2>
                        <p className="mt-4 text-[#a6a6c2] max-w-2xl mx-auto">
                            Drafting tools that respect the structure of a patent application — not generic
                            document automation bolted onto a legal workflow.
                        </p>
                    </motion.div>

                    <motion.div
                        variants={stagger}
                        className="grid md:grid-cols-2 gap-8"
                    >
                        {features.map((feature) => (
                            <motion.div
                                key={feature.title}
                                variants={fadeUp}
                                className="
                                    rounded-lg border border-[#2e2e3a]
                                    shadow-[0_0_0_1px_rgba(255,255,255,0.02)]
                                    bg-[#1c1c24]
                                    bg-[linear-gradient(180deg,rgba(46,46,58,0.35),rgba(28,28,36,0))]
                                    p-8
                                    hover:border-[#c4b5fd]/40
                                    transition
                                "
                            >
                                <div className="w-12 h-12 rounded-lg bg-[#c4b5fd]/10 flex items-center justify-center text-[#c4b5fd] mb-5">
                                    {feature.icon}
                                </div>
                                <h3 className="text-lg font-medium">{feature.title}</h3>
                                <p className="mt-3 text-[#a6a6c2] text-sm leading-relaxed">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>
            </section>

            <section className="border-y border-[#2e2e3a] bg-[#1c1c24] overflow-hidden">
                <div className="mx-auto max-w-6xl px-8 py-20">
                    <motion.h2
                        initial={{opacity: 0, y: 18}}
                        whileInView={{opacity: 1, y: 0}}
                        viewport={{once: true, margin: "-80px"}}
                        transition={{duration: 0.6}}
                        className="text-center text-xl font-medium"
                    >
                        Built by engineers from high-trust systems
                    </motion.h2>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{once: true}}
                        variants={fadeUp}
                    >
                        <motion.div className="mt-12 flex flex-wrap gap-4 justify-center">
                            {[
                                "Meta",
                                "Google",
                                "Microsoft",
                                "U.S. Department of Defense",
                                "Public-sector judicial systems",
                            ].map((name) => (
                                <div
                                    key={name}
                                    className="
                                        relative rounded-full px-6 py-3 text-sm
                                        text-[#ececf3]
                                        border border-[#2e2e3a]
                                        shadow-[0_0_0_1px_rgba(255,255,255,0.02)]
                                        bg-[#1c1c24]
                                        shadow-inner shadow-black/30
                                        hover:-translate-y-0.5 hover:border-[#c4b5fd]/40
                                        transition
                                    "
                                >
                                    <div className="absolute inset-0 rounded-full bg-[#c4b5fd] opacity-[0.035]"/>
                                    <span className="relative z-10 tracking-wide">
                                        {name}
                                    </span>
                                </div>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <section
                id="contact"
                className="mx-auto max-w-6xl px-8 py-32 text-center"
            >
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{once: true, margin: "-100px"}}
                    variants={stagger}
                >
                    <motion.h2 variants={fadeUp} className="text-3xl font-semibold">
                        Let&apos;s talk
                    </motion.h2>

                    <motion.p
                        variants={fadeUp}
                        className="mt-4 mx-auto max-w-xl text-[#a6a6c2]"
                    >
                        Discuss prosecution workflows, drafting bottlenecks, or office action backlogs
                        where AI-assisted drafting can give your practice leverage.
                    </motion.p>

                    <motion.div
                        variants={fadeUp}
                        className="
                            mt-14 rounded-xl overflow-hidden
                            border border-[#2e2e3a]
                            shadow-[0_0_0_1px_rgba(255,255,255,0.02)]
                        "
                    >
                        <iframe
                            src="https://calendly.com/ceyer/ai-document-review-exploration-with-loa?hide_event_type_details=1&hide_gdpr_banner=1"
                            className="w-full h-[720px] bg-[#1c1c24]"
                            frameBorder="0"
                        />
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        className="mt-10 text-sm text-[#a6a6c2]"
                    >
                        Or email{" "}
                        <a
                            href="mailto:ceyer@loa-ai.com"
                            className="underline hover:text-[#ececf3]"
                        >
                            ceyer@loa-ai.com
                        </a>
                    </motion.div>
                </motion.div>
            </section>
            <MaintenanceModal open={showMaintenance} onClose={() => setShowMaintenance(false)}/>
        </main>
    )
}
